import * as React from "react";

import { t } from "@impero-com/common/utils/i18n";
import { Divider, InfoTooltip } from "@impero-com/ui";

import { SubsequentStepIndex, FinancialYearView } from "api/generated/model";

import { PeriodType } from "../common/multiDefaultDueDateOffsetInput";
import { MultiFinancialYearCheckbox } from "../common/multiFinancialYearCheckbox";
import { YearlyRecurrenceRuleWithArrays } from "../common/rulesWithArrays";

abstract class BaseYearlyRecurrenceRuleUpdaterForm<
    P,
> extends React.PureComponent<P> {
    render = () => {
        return (
            <>
                <Divider />
                {this.buildDefaultDueDateOffsetInput()}
                <Divider />
                <h1>
                    {t("CreateControl_Planning_Title")}&nbsp;
                    <InfoTooltip
                        text={t("Control_ModifySchedule_PlanningInfo")}
                    />
                </h1>
                {this.buildResponsibleAllocatedTimeInputs()}
                {this.buildReviewerAllocatedTimeInputs("SubsequentStep0")}
                {this.buildReviewerAllocatedTimeInputs("SubsequentStep1")}
                {this.buildFinancialYearCheckbox()}
                <Divider />
                {this.buildRemindersWidget()}
            </>
        );
    };

    abstract buildDefaultDueDateOffsetInput(): React.ReactNode;

    abstract buildResponsibleAllocatedTimeInputs(): React.ReactNode;

    abstract buildReviewerAllocatedTimeInputs(
        subsequentStepIndex: SubsequentStepIndex
    ): React.ReactNode;

    abstract buildFinancialYearCheckbox(): React.ReactNode;

    abstract buildRemindersWidget(): React.ReactNode;
}

class PartialYearlyRecurrenceRuleUpdater extends BaseYearlyRecurrenceRuleUpdaterForm<PartialYearlyRecurrenceRuleUpdaterProps> {
    buildDefaultDueDateOffsetInput = () =>
        this.props.buildDefaultDueDateOffsetInput("EDIT", "Year");

    buildResponsibleAllocatedTimeInputs = () =>
        this.props.buildResponsibleAllocatedTimeInputs("EDIT");

    buildReviewerAllocatedTimeInputs = (
        subsequentStepIndex: SubsequentStepIndex
    ) =>
        this.props.buildReviewerAllocatedTimeInputs(
            "EDIT",
            subsequentStepIndex
        );

    buildFinancialYearCheckbox = () => {
        const { financialYear, currentRule } = this.props;

        if (financialYear) {
            const values = currentRule.financialYearId?.map((id) => !!id);
            return (
                <MultiFinancialYearCheckbox
                    type="EDIT"
                    values={values}
                    onChange={this.handleFinancialYearChange}
                />
            );
        } else {
            return <></>;
        }
    };

    private handleFinancialYearChange = (checked: boolean) => {
        const { financialYear, onChangeFinancialYear } = this.props;
        let id = null;
        if (checked && financialYear) {
            id = financialYear.id;
        }
        onChangeFinancialYear(id);
    };

    buildRemindersWidget = () => this.props.buildRemindersWidget("EDIT");
}

interface BasePartialYearlyRecurrenceRuleProps {
    financialYear: FinancialYearView | null;
    currentRule: YearlyRecurrenceRuleWithArrays;
    buildDefaultDueDateOffsetInput: (
        readOnly: "EDIT" | "READ_ONLY",
        period: PeriodType
    ) => React.ReactNode;
    buildResponsibleAllocatedTimeInputs: (
        readOnly: "EDIT" | "READ_ONLY"
    ) => React.ReactNode;
    buildReviewerAllocatedTimeInputs: (
        readOnly: "EDIT" | "READ_ONLY",
        subsequentStepIndex: SubsequentStepIndex
    ) => React.ReactNode;
    buildRemindersWidget: (readOnly: "EDIT" | "READ_ONLY") => React.ReactNode;
}

type PartialYearlyRecurrenceRuleUpdaterProps =
    BasePartialYearlyRecurrenceRuleProps & {
        onChangeFinancialYear: (financialYearId: number | null) => void;
    };

type PartialYearlyRecurrenceRuleViewerProps =
    BasePartialYearlyRecurrenceRuleProps;

class PartialYearlyRecurrenceRuleViewer extends BaseYearlyRecurrenceRuleUpdaterForm<PartialYearlyRecurrenceRuleViewerProps> {
    buildDefaultDueDateOffsetInput = () =>
        this.props.buildDefaultDueDateOffsetInput("READ_ONLY", "Year");

    buildResponsibleAllocatedTimeInputs = () =>
        this.props.buildResponsibleAllocatedTimeInputs("READ_ONLY");

    buildReviewerAllocatedTimeInputs = (
        subsequentStepIndex: SubsequentStepIndex
    ) =>
        this.props.buildReviewerAllocatedTimeInputs(
            "READ_ONLY",
            subsequentStepIndex
        );

    buildFinancialYearCheckbox = () => {
        const { financialYear, currentRule } = this.props;

        if (financialYear) {
            const values = currentRule.financialYearId.map((id) => !!id);
            return (
                <MultiFinancialYearCheckbox type="READ_ONLY" values={values} />
            );
        } else {
            return <></>;
        }
    };

    buildRemindersWidget = () => this.props.buildRemindersWidget("READ_ONLY");
}

class FullYearlyRecurrenceRuleUpdater extends BaseYearlyRecurrenceRuleUpdaterForm<FullYearlyRecurrenceRuleUpdaterProps> {
    buildDefaultDueDateOffsetInput = () =>
        this.props.buildDefaultDueDateOffsetInput("EDIT", "Year");

    buildResponsibleAllocatedTimeInputs = () =>
        this.props.buildResponsibleAllocatedTimeInputs("EDIT");

    buildReviewerAllocatedTimeInputs = (
        subsequentStepIndex: SubsequentStepIndex
    ) =>
        this.props.buildReviewerAllocatedTimeInputs(
            "EDIT",
            subsequentStepIndex
        );

    buildFinancialYearCheckbox = () => {
        const { financialYear, currentRule } = this.props;

        if (financialYear) {
            const values = currentRule.financialYearId.map((id) => !!id);
            return (
                <MultiFinancialYearCheckbox
                    type="EDIT"
                    values={values}
                    onChange={this.handleFinancialYearChange}
                />
            );
        } else {
            return <></>;
        }
    };

    private handleFinancialYearChange = (checked: boolean) => {
        const { financialYear, onChangeFinancialYear } = this.props;
        let id = null;
        if (checked && financialYear) {
            id = financialYear.id;
        }
        onChangeFinancialYear(id);
    };

    buildRemindersWidget = () => this.props.buildRemindersWidget("EDIT");
}

interface FullYearlyRecurrenceRuleUpdaterProps {
    financialYear: FinancialYearView | null;
    currentRule: YearlyRecurrenceRuleWithArrays;
    buildDefaultDueDateOffsetInput: (
        readOnly: "EDIT",
        period: PeriodType
    ) => React.ReactNode;
    buildResponsibleAllocatedTimeInputs: (readOnly: "EDIT") => React.ReactNode;
    buildReviewerAllocatedTimeInputs: (
        readOnly: "EDIT",
        subsequentStepIndex: SubsequentStepIndex
    ) => React.ReactNode;
    buildRemindersWidget: (readOnly: "EDIT") => React.ReactNode;
    onChangeFinancialYear: (financialYearId: number | null) => void;
}
export type {
    BasePartialYearlyRecurrenceRuleProps,
    PartialYearlyRecurrenceRuleViewerProps,
    FullYearlyRecurrenceRuleUpdaterProps,
};
export {
    PartialYearlyRecurrenceRuleUpdater,
    PartialYearlyRecurrenceRuleViewer,
    FullYearlyRecurrenceRuleUpdater,
};
